import type { ThemeConfig } from 'antd';

import { color, focus, fontFamily, palette, radius, shadow } from '@/lib/design-tokens';

// Shared Figma field treatment for text-like inputs (spec: 40px default / 46px
// tall, radius 10, subtle border, brand-green focus ring, error-900 border).
const fieldTokens = {
  controlHeight: 40,
  controlHeightLG: 46,
  borderRadius: 10,
  borderRadiusLG: 10,
  paddingInline: 15,
  colorBorder: color.border.subtle,
  hoverBorderColor: color.border.default,
  activeBorderColor: color.border.interactive,
  activeShadow: focus.field,
  errorActiveShadow: focus.fieldError,
  colorError: palette.error[900],
  colorTextPlaceholder: palette.overlay[20],
  colorBgContainerDisabled: palette.neutral[100],
  colorTextDisabled: palette.overlay[20],
};

export const trackingTheme: ThemeConfig = {
  token: {
    colorPrimary: color.brand.base,
    colorSuccess: color.status.success,
    colorWarning: color.status.warning,
    colorError: color.status.error,
    colorInfo: color.status.info,

    colorBgLayout: color.surface.page,
    colorBgContainer: color.surface.card,
    colorText: color.text.primary,
    colorTextSecondary: color.text.secondary,
    colorBorder: color.border.default,
    colorBorderSecondary: color.border.subtle,
    colorBgMask: palette.overlay[20],

    fontFamily: fontFamily.body,
    fontFamilyCode: fontFamily.mono,
    borderRadius: Number.parseInt(radius.md, 10),
    boxShadow: shadow.overlay,
    boxShadowSecondary: shadow.surface,
  },
  components: {
    Layout: { headerHeight: 56 },
    Input: fieldTokens,
    InputNumber: fieldTokens,
    Button: {
      controlHeight: 40,
      controlHeightLG: 48,
      controlHeightSM: 32,
      borderRadius: 999,
      borderRadiusLG: 999,
      borderRadiusSM: 999,
      paddingInline: 20,
      paddingInlineLG: 20,
      paddingInlineSM: 20,
      contentFontSize: 13,
      contentFontSizeLG: 15,
      contentFontSizeSM: 11,
      fontWeight: 500,
    },
    Tabs: {
      inkBarColor: color.border.interactive,
      itemColor: color.text.muted,
      itemSelectedColor: color.text.primary,
      itemHoverColor: color.text.secondary,
      titleFontSize: 14,
    },
    Notification: {
      borderRadiusLG: 16,
      boxShadow: shadow.overlay,
      colorBgElevated: color.surface.page,
      paddingMD: 10,
      paddingContentHorizontalLG: 16,
    },
    Checkbox: {
      // Figma 134:2331 — 16px box, radius 4, dark (not brand-green) fill.
      controlInteractiveSize: 16,
      borderRadiusSM: 4,
      colorPrimary: palette.neutral[950],
      colorPrimaryHover: palette.neutral[800],
      colorBorder: palette.neutral[400],
    },
    Radio: {
      // Figma 135:1811 — 16px circle, dark ring + dot.
      radioSize: 16,
      dotSize: 7,
      colorPrimary: palette.neutral[950],
      colorBorder: palette.neutral[400],
    },
    Switch: {
      // Figma 134:1974 — md maps to antd default, sm to antd small; lg is CSS-only.
      trackHeight: 20,
      trackMinWidth: 40,
      handleSize: 16,
      trackHeightSM: 16,
      trackMinWidthSM: 32,
      handleSizeSM: 12,
      trackPadding: 2,
      colorPrimary: palette.brand[500],
      colorPrimaryHover: palette.brand[500],
      handleBg: palette.brand[50],
      handleShadow: shadow.switch,
      // Off-state track: not in Figma — neutral assumption, flagged in spec.
      colorTextQuaternary: palette.neutral[300],
      colorTextTertiary: palette.neutral[400],
    },
    Tooltip: {
      borderRadius: 12,
      fontSize: 12,
      colorBgSpotlight: palette.neutral[950],
      colorTextLightSolid: palette.neutral[50],
    },
    Select: {
      ...fieldTokens,
      controlHeightSM: 36,
      optionHeight: 36,
      optionSelectedBg: palette.neutral[100],
      multipleItemBg: palette.neutral[100],
      multipleItemHeight: 18,
    },
    DatePicker: {
      ...fieldTokens,
      // Dark selected cell, matching the Checkbox/Radio "dark, not brand-green"
      // selection treatment. The Figma calendar panel (node 2510:386577) is the
      // unstyled shadcn placeholder; we map it to Greenway tokens instead.
      colorPrimary: palette.neutral[950],
      cellHeight: 36,
      cellWidth: 40,
      borderRadiusSM: 8,
      fontFamily: fontFamily.body,
    },
    Table: {
      headerBg: color.surface.tableHeader,
      headerColor: color.text.tertiary,
      headerSplitColor: 'transparent',
      headerBorderRadius: 8,
      cellFontSize: 14,
      cellPaddingInline: 20,
      borderColor: color.border.subtle,
      rowHoverBg: palette.neutral[50],
    },
  },
};
